import React from 'react';

import { Link } from '~/lib/react-router-dom';

import { Stack, Tooltip, Typography } from '@mui/material';

import { CreateSinglePromoClonePayload } from '~/hooks/usePromo';

import { BulkCreatePromosResult, PromoMeta } from '~/services/PromoService';
import { formatDateLongMonthWithMeridiemNoSeconds } from '~/services/TimeService';

import PageSubheader from '../Common/Elements/PageSubheader';
import UserName from '../Common/UserName';
import DebugModal from '../DebugModal/DebugModal';
import { promoIdPath } from '../Promo/promoUrls';

import PromoCopyButton from './PromoCopyButton';

type PromoSummaryActionsProps = {
  promoCode: App.PromoData;
  parentPromoCode: App.PromoData | null;
  promoMeta: PromoMeta | null;
  createSinglePromoClone?: (payload: CreateSinglePromoClonePayload) => Promise<BulkCreatePromosResult>;
};

/** User id of the admin who last saved the promo (API may expose `modified_by` and/or legacy `updated_by`). */
function getPromoLastModifiedByUserId(promo: App.PromoData): string | undefined {
  const extended = promo as App.PromoData & { updated_by?: string };
  const id = extended.modified_by?.trim() || extended.updated_by?.trim();
  return id || undefined;
}

const PromoSummaryActions = ({
  promoCode,
  parentPromoCode,
  promoMeta,
  createSinglePromoClone,
}: PromoSummaryActionsProps) => {
  const lastModifiedByUserId = promoCode ? getPromoLastModifiedByUserId(promoCode) : undefined;
  const createdFormatted = promoCode?.created_at
    ? formatDateLongMonthWithMeridiemNoSeconds(promoCode.created_at)
    : undefined;
  const updatedFormatted = promoCode?.updated_at
    ? formatDateLongMonthWithMeridiemNoSeconds(promoCode.updated_at)
    : undefined;
  // Compare raw API timestamps: formatted LLL can match within the same clock minute while ISO strings differ.
  const showUpdatedRow = Boolean(promoCode?.updated_at) && promoCode.updated_at !== promoCode.created_at;

  return (
    <PageSubheader title="Promo code details">
      {promoCode && promoCode.cloned_from_promo_id && (
        <Link target="_blank" to={promoIdPath(promoCode.cloned_from_promo_id)}>
          {`[child of ${parentPromoCode?.code_name ?? promoCode.cloned_from_promo_id}]`}
        </Link>
      )}
      {promoCode?.email_addresses &&
        promoCode?.email_addresses.length > 0 &&
        promoCode?.email_addresses?.join(',').length < 200 && (
          <Typography variant="subtitle2">[for {promoCode.email_addresses.join(', ')}]</Typography>
        )}
      <Stack direction="column" spacing={0.5}>
        <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
          {promoCode?.added_by && (
            <Tooltip title="Created">
              <span>
                <UserName asLink userId={promoCode.added_by} />
              </span>
            </Tooltip>
          )}
          {promoCode?.added_by && promoCode?.created_at && (
            <Typography variant="subtitle2" component="span">
              @
            </Typography>
          )}
          {promoCode?.created_at && (
            <Typography
              variant="subtitle2"
              component="span"
              title={promoCode.created_at ? `Created at: ${promoCode.created_at}` : undefined}
            >
              {createdFormatted}
            </Typography>
          )}
        </Stack>

        {showUpdatedRow && promoCode?.updated_at && (
          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
            <Typography
              variant="subtitle2"
              component="span"
              title={promoCode.updated_at ? `Updated at: ${promoCode.updated_at}` : undefined}
            >
              Updated {updatedFormatted}
            </Typography>
            {lastModifiedByUserId ? (
              <>
                <Typography variant="subtitle2" component="span">
                  by
                </Typography>
                <Tooltip title="Updated">
                  <span>
                    <UserName asLink userId={lastModifiedByUserId} />
                  </span>
                </Tooltip>
              </>
            ) : null}
          </Stack>
        )}

        <Stack direction="row" flexWrap="wrap">
          {promoCode && (
            <DebugModal
              type="generic"
              title="Raw Promo Data"
              data={JSON.parse(
                JSON.stringify({
                  promoCode: promoCode,
                  parentPromoCode: parentPromoCode,
                  promoMeta: promoCode.meta,
                  promoDiscounts: promoCode.promo_discounts,
                }),
              )}
            />
          )}
          {parentPromoCode && (
            <DebugModal
              type="generic"
              title="Raw Parent Promo Data"
              data={JSON.parse(JSON.stringify(parentPromoCode))}
            />
          )}
          {/* No copy on child promos: we only allow one clone generation from the parent (no chained child→child copies). */}
          {promoCode && createSinglePromoClone && !promoCode.cloned_from_promo_id && (
            <PromoCopyButton
              isLoading={false}
              promoId={promoCode?.id_promo_code}
              codeName={promoCode?.code_name}
              parentCodeName={parentPromoCode?.code_name}
              promoToggles={promoCode?.promo_toggles}
              createSinglePromoClone={createSinglePromoClone}
            />
          )}
        </Stack>
      </Stack>
    </PageSubheader>
  );
};

export default PromoSummaryActions;
